import type { FileRef, FormDefinition, SubmissionInput } from "@fobu/schema";
import type { AnswerIssue } from "@fobu/engine";

export interface PublishedForm {
  /** ID der veröffentlichten Version – geht mit der Einreichung zurück an den Server. */
  versionId: string;
  definition: FormDefinition;
  /** Vom Server: Restplätze je Option (`fieldId:choiceId` → frei). */
  remaining?: Record<string, number>;
  closed?: boolean;
  /** Persönliche Einladung (?i=…): zum Vorausfüllen und für den Status. */
  invite?: { name: string | null; email: string; completed: boolean };
  /** Fortsetzungslink (?r=…): gespeicherter Zwischenstand. */
  resume?: ResumeState;
}

export interface ResumeState {
  answers: Record<string, unknown>;
  other: Record<string, string>;
  hidden?: Record<string, string>;
  position: string | null;
  savedAt: string;
  expiresAt: string;
}

export interface ResumeLink {
  token: string;
  url: string;
  expiresAt: string;
  emailed: boolean;
}

export type SubmitResult =
  | { ok: true; endingId?: string; redirectUrl?: string; waitlisted?: boolean; updated?: boolean; payment?: { token: string; amountCents: number; currency: string; status: string }; resultsToken?: string }
  | { ok: false; errors?: Record<string, AnswerIssue[]>; message?: string };

/** Abstraktion über die Datenquelle – lokal (Demo/Vorschau) oder HTTP-API. */
/** Was der Renderer schickt: Einreichung plus zufällige Sitzungs-ID (verknüpft Teilantworten). */
export type OutgoingSubmission = SubmissionInput & {
  sessionId?: string;
  /** Je Absendevorgang: Wiederholungen mit demselben Schlüssel erzeugen keine zweite Antwort. */
  submissionKey?: string;
  /** Fortsetzungslink, dessen Zwischenstand nach dem Absenden gelöscht wird. */
  resumeToken?: string;
  /** Zeitpunkt des Ladens (ms) – für Ausfüllzeit und Spam-Schutz. */
  startedAt?: number;
  /** Honeypot: von Menschen nie ausgefüllt. */
  _hp?: string;
  /** Nachweise bestätigter E-Mail-Adressen je Feld. */
  verifications?: Record<string, string>;
};

export interface LiveEndpoint {
  base: string;
  headers: () => Record<string, string>;
}

/** Ergebnis beim Anfordern/Prüfen eines Bestätigungscodes. */
export type CodeResult = { ok: true; proof?: string } | { ok: false; status: number; message?: string };

export interface FormClient {
  submit(versionId: string, input: OutgoingSubmission): Promise<SubmitResult>;
  upload(versionId: string, file: File, fieldId: string): Promise<FileRef>;
  /** Teilantworten für Drop-off-Analyse; darf still fehlschlagen. */
  savePartial?(versionId: string, input: OutgoingSubmission & { pageId: string }): Promise<void>;
  /** Erste Interaktion melden (Trichter); darf still fehlschlagen. */
  markStarted?(versionId: string): void;
  /** „Später weitermachen“: Zwischenstand auf dem Server sichern (nur wenn das Formular es erlaubt). */
  saveResume?(versionId: string, input: { answers: Record<string, unknown>; other: Record<string, string>; hidden: Record<string, string>; position: string | null; token?: string; email?: string }): Promise<ResumeLink>;
  /** Optionen eines Auswahlfelds aus einer Datenquelle (nur erlaubte Spalten). */
  lookup?(versionId: string, field: LookupFieldDef, parent: string | null): Promise<LookupResult>;
  /** Bisherige Ergebnisse vor dem Abstimmen (nur wenn im Formular freigegeben). */
  liveResults?(versionId: string, lang: string): Promise<unknown>;
  /** E-Mail-Adresse per Code bestätigen (fehlt in Vorschau/Test – dort wird nicht bestätigt). */
  emailCode?(versionId: string, fieldId: string, email: string, lang: string): Promise<CodeResult>;
  /** Live-Abstimmung (nur veröffentlichte Formulare; in Vorschau/Test nicht vorhanden). */
  live?: LiveEndpoint;
  verifyEmailCode?(versionId: string, fieldId: string, email: string, code: string): Promise<CodeResult>;
}

async function codeCall(url: string, body: unknown, headers: Record<string, string> = {}): Promise<CodeResult> {
  try {
    const r = await fetch(url, { method: "POST", headers: { ...headers, "content-type": "application/json" }, body: JSON.stringify(body) });
    const b = (await r.json().catch(() => ({}))) as { proof?: string; message?: string };
    return r.ok ? { ok: true, ...(b.proof ? { proof: b.proof } : {}) } : { ok: false, status: r.status, ...(b.message ? { message: b.message } : {}) };
  } catch {
    return { ok: false, status: 0 };
  }
}
const codeUrl = (versionId: string, verify = false) => `/api/v1/public/versions/${encodeURIComponent(versionId)}/email-code${verify ? "/verify" : ""}`;
export const emailCodeApi = (headers: () => Record<string, string>) => ({
  emailCode: (versionId: string, fieldId: string, email: string, lang: string) => codeCall(codeUrl(versionId), { fieldId, email, lang }, headers()),
  verifyEmailCode: (versionId: string, fieldId: string, email: string, code: string) => codeCall(codeUrl(versionId, true), { fieldId, email, code }, headers()),
});

export type LookupFieldDef = Extract<import("@fobu/schema").Field, { type: "lookup" }>;
export interface LookupResult {
  options: Array<{ value: string; label: string; fill?: Record<string, string> }>;
  truncated: boolean;
}

export async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, init);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return (await r.json()) as T;
}

/** Veröffentlichte Fassung: öffentliche Optionen je Feld. */
export const publicLookup = (versionId: string, field: LookupFieldDef, parent: string | null, headers: Record<string, string> = {}) =>
  getJson<LookupResult>(`/api/v1/public/versions/${encodeURIComponent(versionId)}/lookup/${encodeURIComponent(field.id)}${parent !== null ? `?parent=${encodeURIComponent(parent)}` : ""}`, { headers });

export const randomId = () => (typeof globalThis.crypto?.randomUUID === "function" ? crypto.randomUUID() : Math.random().toString(36).slice(2));

export class PasswordRequiredError extends Error {
  constructor(
    readonly title: string,
    readonly theme: FormDefinition["theme"],
  ) {
    super("Passwort nötig");
    this.name = "PasswordRequiredError";
  }
}

export class InviteRequiredError extends Error {
  constructor(readonly title: string) {
    super("Einladung nötig");
    this.name = "InviteRequiredError";
  }
}

export class FormNotFoundError extends Error {
  constructor() {
    super("Formular nicht gefunden");
    this.name = "FormNotFoundError";
  }
}

/** Gegen die Fobu-API (folgt mit apps/api). */
export class HttpClient implements FormClient {
  /** Zugangs-Token für passwortgeschützte Formulare. */
  private access: string | null = null;
  /** Token aus dem persönlichen Einladungslink. */
  private invite: string | null = typeof location !== "undefined" ? new URLSearchParams(location.search).get("i") : null;

  constructor(private readonly base = "/api/v1") {}

  private codes = emailCodeApi(() => this.headers());
  emailCode = this.codes.emailCode;
  verifyEmailCode = this.codes.verifyEmailCode;

  /** Live-Abstimmung: Adresse und Kopfzeilen; die Aufrufe selbst stecken im nachgeladenen Teil. */
  get live(): LiveEndpoint {
    return { base: this.base, headers: () => this.headers() };
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    return { ...extra, ...(this.access ? { "x-fobu-access": this.access } : {}) };
  }

  lookup(versionId: string, field: LookupFieldDef, parent: string | null) {
    return publicLookup(versionId, field, parent, this.headers());
  }

  liveResults(versionId: string, lang: string) {
    return getJson(`${this.base}/public/versions/${encodeURIComponent(versionId)}/live-results?lang=${encodeURIComponent(lang)}`, { headers: this.headers() });
  }

  async load(slug: string, resumeToken?: string | null): Promise<PublishedForm> {
    this.access ??= sessionGet(`fobu:access:${slug}`);
    const params = new URLSearchParams();
    if (this.invite) params.set("i", this.invite);
    if (resumeToken) params.set("r", resumeToken);
    const qs = params.toString();
    const q = qs ? `?${qs}` : "";
    const res = await fetch(`${this.base}/public/forms/${encodeURIComponent(slug)}${q}`, { headers: this.headers({ accept: "application/json" }) });
    if (res.status === 404) throw new FormNotFoundError();
    if (res.status === 403) {
      const b = (await res.json().catch(() => ({}))) as { title?: string };
      throw new InviteRequiredError(b.title ?? "");
    }
    if (res.status === 401) {
      const b = (await res.json().catch(() => ({}))) as { title?: string; theme?: FormDefinition["theme"] };
      throw new PasswordRequiredError(b.title ?? "", b.theme as FormDefinition["theme"]);
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as PublishedForm;
  }

  /** Passwort prüfen; bei Erfolg gilt das Token für diese Sitzung. */
  async unlock(slug: string, password: string): Promise<boolean> {
    const res = await fetch(`${this.base}/public/forms/${encodeURIComponent(slug)}/unlock`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ password }) });
    if (!res.ok) return false;
    this.access = ((await res.json()) as { token: string }).token;
    sessionSet(`fobu:access:${slug}`, this.access);
    return true;
  }

  markStarted(versionId: string) {
    const key = `fobu:started:${versionId}`;
    if (sessionGet(key)) return;
    sessionSet(key, "1");
    void fetch(`${this.base}/public/versions/${encodeURIComponent(versionId)}/start`, { method: "POST", keepalive: true }).catch(() => {});
  }

  async submit(versionId: string, input: OutgoingSubmission): Promise<SubmitResult> {
    let res: Response | null = null;
    // Netzwerkfehler: kurz automatisch wiederholen. Dank `submissionKey` entsteht dabei nie eine zweite Antwort.
    const attempts = input.submissionKey ? 3 : 1;
    for (let i = 0; i < attempts && !res; i++) {
      if (i) await new Promise((r) => setTimeout(r, 600 * i));
      try {
        res = await fetch(`${this.base}/public/versions/${encodeURIComponent(versionId)}/submissions`, {
          method: "POST",
          headers: this.headers({ "content-type": "application/json" }),
          body: JSON.stringify(this.invite ? { ...input, inviteToken: this.invite } : input),
        });
      } catch {
        res = null;
      }
    }
    if (!res) return { ok: false, message: "network" };
    if (res.ok) return (await res.json()) as SubmitResult;
    if (res.status === 429) return { ok: false, message: "rate_limited" };
    if (res.status === 410) return { ok: false, message: "closed" };
    if (res.status === 409 && this.invite) {
      const b = (await res.clone().json().catch(() => ({}))) as { errors?: unknown; message?: string };
      if (!b.errors) return { ok: false, message: "invite_used" };
    }
    // 422 = Feldfehler, 409 = Kontingent gerade ausgebucht
    if (res.status === 422 || res.status === 409) {
      const body = (await res.json().catch(() => ({}))) as { errors?: Record<string, AnswerIssue[]> };
      return { ok: false, ...(body.errors ? { errors: body.errors } : { message: "network" }) };
    }
    return { ok: false, message: "network" };
  }

  async upload(versionId: string, file: File, fieldId: string): Promise<FileRef> {
    const body = new FormData();
    body.append("file", file);
    body.append("fieldId", fieldId);
    const res = await fetch(`${this.base}/public/versions/${encodeURIComponent(versionId)}/uploads`, { method: "POST", body, headers: this.headers() });
    if (!res.ok) throw new Error(`Upload fehlgeschlagen (HTTP ${res.status})`);
    return (await res.json()) as FileRef;
  }

  async saveResume(versionId: string, input: Parameters<NonNullable<FormClient["saveResume"]>>[1]): Promise<ResumeLink> {
    const res = await fetch(`${this.base}/public/versions/${encodeURIComponent(versionId)}/resume`, {
      method: "POST",
      headers: this.headers({ "content-type": "application/json" }),
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as ResumeLink;
  }

  async savePartial(versionId: string, input: OutgoingSubmission & { pageId: string }): Promise<void> {
    try {
      await fetch(`${this.base}/public/versions/${encodeURIComponent(versionId)}/partials`, {
        method: "POST",
        headers: this.headers({ "content-type": "application/json" }),
        body: JSON.stringify(input),
        keepalive: true,
      });
    } catch {
      /* Teilantworten sind best effort */
    }
  }
}

function sessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function sessionSet(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* privates Fenster o. Ä. */
  }
}
